import isEmpty from 'lodash/isEmpty';
import upperFirst from 'lodash/upperFirst';
import { LoaderCircleIcon } from 'lucide-react';
import moment from 'moment';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link as RouterLink, useHistory } from 'react-router-dom';

import { Student } from '../../../../../api/types/student';
import { SubscriptionDetails } from '../../../../../api/types/subscription';
import FilledExclamation from '../../../../../assets/images/icon-filled-alert-exclamation.svg';
import { openStudentHubWithSso } from '../../../../../util/openStudentHubWithSso';
import { PATH_HUB_ADD_STUDENT, PATH_HUB_SUBSCRIPTIONS } from '../../../../../util/pagePath';
import { cn } from '../../../../../v2/common/helpers/cn';
import { StudentSubscriptionLabel } from '../../../../../v2/common/hooks/useStudentSubscriptionLabel';
import { Button } from '../../../../../v2/components/ui/button';
import api from '../../../../../v2/legacy/api';

const PLACEHOLDER_STUDENT_PATH = (studentId: string) => `/portal/customer/students/${studentId}/profile`;

export const StudentCardSkeleton = () => {
  return (
    <div className="flex animate-pulse flex-col gap-2 rounded-lg border border-grey-2 bg-grey-1 p-4">
      <div className="h-4 w-1/2 rounded-full bg-grey-2"></div>
      <div className="h-4 w-1/4 rounded-full bg-grey-2"></div>
      <div className="h-4 w-1/3 rounded-full bg-grey-2"></div>
    </div>
  );
};

const hasValidSubscription = (subscriptions: SubscriptionDetails[]) =>
  subscriptions.some(
    (subscription) =>
      subscription.subscriptionPaidUntilDate === null ||
      moment(subscription.subscriptionPaidUntilDate).isSameOrAfter(moment(), 'day')
  );

export const StudentCard = ({
  student,
  className,
  getStudentSubscriptionLabel,
}: {
  student: Student;
  className?: string;
  getStudentSubscriptionLabel: (studentId: string) => StudentSubscriptionLabel | null;
}) => {
  const history = useHistory();
  const subscriptionLabel = getStudentSubscriptionLabel(student.studentId);

  const { data: subscriptionsData } = api.subscription.getAll.useQuery(undefined, {
    enabled: !!student.studentId,
  });

  const [isOpeningStudentHub, setIsOpeningStudentHub] = useState(false);

  return (
    <div
      className={cn(
        'flex w-60 flex-shrink-0 flex-col gap-2 rounded-lg border border-grey-2 bg-white p-4 text-sm shadow-md md:w-auto lg:flex-shrink',
        className
      )}
    >
      {isOpeningStudentHub &&
        createPortal(
          <div
            className="fixed inset-0 z-40 flex items-center justify-center bg-background/80 backdrop-blur-sm"
            data-testid="admin-sso-loading-backdrop"
            aria-busy="true"
            aria-live="polite"
          >
            <LoaderCircleIcon className="size-8 animate-spin text-muted-foreground" aria-hidden />
            <span className="sr-only">Opening manage account</span>
          </div>,
          document.body
        )}
      <div className="flex flex-row justify-between text-base font-semibold text-grey-600">
        <>
          {student.firstName}
          {student.currentYearLevel && ` | ${student.currentYearLevel}`}
        </>
        <RouterLink
          to={PLACEHOLDER_STUDENT_PATH(student.studentId)}
          className="text-base font-normal text-orange-600 underline underline-offset-2"
        >
          See profile
        </RouterLink>
      </div>
      <div className="flex-grow pb-2 text-grey-600">
        {!isEmpty(student.enrolments) ? (
          <>
            <p>Currently learning:</p>
            <p>{Object.keys(student.enrolments).map(upperFirst).join(', ')}</p>
          </>
        ) : (
          <p className="flex items-center">
            <img src={FilledExclamation} alt="warning" className="mr-2 h-4 w-4" /> No learning in progress
          </p>
        )}
      </div>
      <div>
        <Button
          className={cn(
            'flex h-10 w-full items-center gap-2 bg-transparent !pr-2 text-grey-400 hover:!bg-grey-100',
            subscriptionLabel?.buttonClassName,
            subscriptionLabel?.text && subscriptionLabel?.className && 'text-center'
          )}
          onClick={async () => {
            const studentSubscriptions = subscriptionsData?.find((sub) => sub.studentId === student.studentId);
            if (hasValidSubscription(studentSubscriptions?.subscriptions ?? [])) {
              setIsOpeningStudentHub(true);
              await openStudentHubWithSso({
                target: '_self',
                returnTo: `/?student=${student.customerNumber}`,
              });
              return;
            }
            history.push(PATH_HUB_SUBSCRIPTIONS, { studentId: student.studentId });
          }}
        >
          <span className="text-sm font-medium">{subscriptionLabel?.buttonText}</span>
          {subscriptionLabel?.text && subscriptionLabel?.className && (
            <span
              className={cn(
                'ml-auto shrink-0 whitespace-nowrap rounded-full px-2 py-1 text-xs font-semibold text-grey-600',
                subscriptionLabel?.className
              )}
            >
              {subscriptionLabel?.text}
            </span>
          )}
        </Button>
      </div>
    </div>
  );
};

export const AddAStudentCard = ({ className }: { className?: string } = {}) => {
  return (
    <RouterLink
      to={PATH_HUB_ADD_STUDENT}
      className={cn(
        'flex w-60 flex-shrink-0 snap-center flex-col items-center justify-center gap-y-3 rounded-lg border-2 border-dashed border-grey-2 bg-white p-4 text-sm hover:bg-brown-100 md:w-auto',
        className
      )}
    >
      <div className="link text-base leading-4 tracking-normal underline underline-offset-0">+ Add a student</div>
      <div className="w-3/4 text-center text-sm leading-5 tracking-normal text-grey-600">
        Access unique content tailored to your specific needs
      </div>
    </RouterLink>
  );
};
